/**
 * DCTA-87 — the caching service worker is retired. We do not need offline, and a worker that
 * answers asset requests from its own store is one more place where a phone can hold a
 * different build than the bank serves (build 13 drew with no CSS on a Home Screen app
 * installed on build 11).
 *
 * Two halves:
 * - `installRetiringWorker` is the whole of `/sw.js`: a kill switch. `sw.js` is served
 *   `no-cache`, so a phone still controlled by the old worker fetches this one on its next
 *   open; it takes over at once, deletes every cache, unregisters itself and reloads its
 *   windows. It has NO fetch handler: while it lives, every request goes to the network.
 * - `retireFromPage` runs in the page: it registers nothing and unregisters whatever it finds.
 *
 * The scopes are declared structurally (not from `lib.webworker`, which cannot be mixed with
 * the DOM lib in one project) and are the test seam.
 */

interface WindowClientLike {
  readonly url: string;
  navigate(url: string): Promise<unknown>;
}

interface CacheNames {
  keys(): Promise<readonly string[]>;
  delete(name: string): Promise<boolean>;
}

export interface RetiringScope {
  readonly caches: CacheNames;
  readonly registration: { unregister(): Promise<boolean> };
  readonly clients: { matchAll(options?: { type?: "window" }): Promise<readonly WindowClientLike[]> };
  skipWaiting(): Promise<void>;
  addEventListener(type: string, listener: (event: Event) => void): void;
}

interface ExtendableEventLike extends Event {
  waitUntil(promise: Promise<unknown>): void;
}

async function emptyCaches(caches: CacheNames): Promise<void> {
  const names = await caches.keys();
  await Promise.all(names.map((name) => caches.delete(name)));
}

async function retire(scope: RetiringScope): Promise<void> {
  await emptyCaches(scope.caches);
  await scope.registration.unregister();
  const windows = await scope.clients.matchAll({ type: "window" });
  // Reload each window so it comes back uncontrolled, from the network. One that refuses
  // (not controlled by us, already gone) must not keep the others on the old page.
  await Promise.all(windows.map((w) => w.navigate(w.url).catch(() => undefined)));
}

export function installRetiringWorker(scope: RetiringScope): void {
  scope.addEventListener("install", (event) => {
    (event as ExtendableEventLike).waitUntil(scope.skipWaiting());
  });
  scope.addEventListener("activate", (event) => {
    (event as ExtendableEventLike).waitUntil(retire(scope));
  });
}

interface NavigatorLike {
  readonly serviceWorker?: {
    readonly controller?: unknown;
    getRegistrations(): Promise<readonly { unregister(): Promise<boolean> }[]>;
  };
}

/**
 * Unregister every service worker this origin has and empty its caches. Resolves to the
 * number of registrations removed; never rejects (a private window or a blocked storage
 * throws here, and the page must draw regardless).
 *
 * A page the old worker already CONTROLS stays controlled after unregister — the worker keeps
 * answering (and caching) its requests until the page goes (found by e2e/sw-retire.spec.ts).
 * So such a page calls `reload` once: an unregistered scope does not control the next
 * navigation.
 */
export async function retireFromPage(
  nav: NavigatorLike,
  caches: CacheNames | undefined,
  reload?: () => void,
): Promise<number> {
  let removed = 0;
  const controlled = nav.serviceWorker?.controller != null;
  try {
    const registrations = (await nav.serviceWorker?.getRegistrations()) ?? [];
    await Promise.all(registrations.map((r) => r.unregister().then((ok) => void (ok && removed++))));
  } catch {
    // nothing to retire, or not allowed to look
  }
  try {
    if (caches !== undefined) await emptyCaches(caches);
  } catch {
    // storage blocked
  }
  if (controlled && removed > 0) reload?.();
  return removed;
}

const RELOADED = "dcta-sw-retired";

/**
 * `reload`, at most once per session. Where the session cannot remember that it reloaded
 * (storage blocked), it does not reload at all: a loop is worse than one page drawn under
 * the old worker, which the CSS in the HTML already survives.
 */
export function reloadOncePerSession(
  session: Pick<Storage, "getItem" | "setItem"> | undefined,
  reload: () => void,
): () => void {
  return () => {
    try {
      if (session === undefined || session.getItem(RELOADED) !== null) return;
      session.setItem(RELOADED, "1");
    } catch {
      return;
    }
    reload();
  };
}

/** `window.sessionStorage`, or undefined where reading the property itself throws (blocked storage). */
export function sessionStorageOrUndefined(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}
